import { z } from "zod";

/**
 * Mirrors the API's `ApiHealthStatus`. Kept as a Zod enum rather than a loose string so a provider
 * status the UI has no colour for fails validation here instead of rendering as a blank chip.
 */
export const ApiHealthStatusSchema = z.enum([
	"OK",
	"DRIFT",
	"INPUT_REJECTED",
	"EMPTY",
	"BREAKING",
	"DOWN",
]);

export const ApiHealthCheckModeSchema = z.enum(["ACTIVE", "PASSIVE"]);

export const ApiHealthMonitoringSchema = z.enum(["PROBED", "PASSIVE_ONLY"]);

export const ContractViolationSchema = z.object({
	severity: z.string(),
	path: z.string(),
	message: z.string(),
});

export const ApiHealthEndpointSchema = z.object({
	contractKey: z.string(),
	host: z.string(),
	endpoint: z.string(),
	label: z.string(),
	// Null means "never checked", which the page must show as unknown rather than healthy.
	status: ApiHealthStatusSchema.nullable(),
	mode: ApiHealthCheckModeSchema.nullable(),
	monitoring: ApiHealthMonitoringSchema,
	checkedAt: z.coerce.date().nullable(),
	latencyMs: z.number().nullable(),
	httpStatus: z.number().nullable(),
	sampleSize: z.number().nullable(),
	sampleRequestId: z.number().nullable(),
	errorMessage: z.string().nullable(),
	violations: z.array(ContractViolationSchema),
});

export const ApiHealthHostSchema = z.object({
	host: z.string(),
	healthyCount: z.number(),
	endpointCount: z.number(),
	worstStatus: ApiHealthStatusSchema.nullable(),
	endpoints: z.array(ApiHealthEndpointSchema),
});

export const ApiHealthCostSchema = z.object({
	callsPerMonth: z.number(),
	costPerMonth: z.number(),
	unpricedEndpoints: z.array(z.string()),
});

export const ApiHealthOverviewSchema = z.object({
	hosts: z.array(ApiHealthHostSchema),
	probesEnabled: z.boolean(),
	probeCost: ApiHealthCostSchema,
	generatedAt: z.coerce.date(),
});

export const ApiHealthHistoryEntrySchema = z.object({
	status: ApiHealthStatusSchema,
	mode: ApiHealthCheckModeSchema,
	checkedAt: z.coerce.date(),
	latencyMs: z.number().nullable(),
	httpStatus: z.number().nullable(),
	sampleRequestId: z.number().nullable(),
	errorMessage: z.string().nullable(),
	violations: z.array(ContractViolationSchema),
});

export const ApiHealthHistorySchema = z.object({
	contractKey: z.string(),
	label: z.string(),
	entries: z.array(ApiHealthHistoryEntrySchema),
});

export type ApiHealthStatus = z.infer<typeof ApiHealthStatusSchema>;
export type ApiHealthEndpoint = z.infer<typeof ApiHealthEndpointSchema>;
export type ApiHealthHost = z.infer<typeof ApiHealthHostSchema>;
export type ApiHealthOverview = z.infer<typeof ApiHealthOverviewSchema>;
export type ApiHealthHistory = z.infer<typeof ApiHealthHistorySchema>;
export type ApiHealthHistoryEntry = z.infer<typeof ApiHealthHistoryEntrySchema>;
